/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import Preview from "./Preview";
import { DeleteModal } from "./DeleteModal";
import Service from "../../../../api/service";
import { RiRefreshLine, RiSearchLine } from "react-icons/ri";
import { TextArea } from "../../component/FormUtils";
import { MyButton } from "../../component/ButtonUtils";
import { Pagination } from "../../component/Pagination";
import { MyMessage } from "../../component/messageUtils";
import { _PAGE_SIZE_ } from "../../../../middlewares/other/tools";
import { MyActionButton } from "../../component/ActionButtonUtils";
import { FaRegCheckSquare, FaRegSquare, FaRegTrashAlt } from "react-icons/fa";

const Overview = () => {
  const [chats, setChats] = useState(null);
  const [count, setCount] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [promptId, setPromptId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [chatsLoading, setChatsLoading] = useState(false); // UI-only
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(_PAGE_SIZE_);

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [success, setSuccess] = useState(false);

  const [selectedChatIds, setSelectedChatIds] = useState([]);
  const [query, setQuery] = useState(""); // UI-only search

  useEffect(() => {
    if (!chats) getChats(page, pageSize);
    if (!prompt) getPrompt();
  }, [chats, prompt]);

  const getChats = async (p = 1, size = 50) => {
    setChatsLoading(true);
    const url = `/api/v1/ai-chat/?page=${p}&pageSize=${size}`;
    const { data } = await Service.get(url);

    if (data) {
      setCount(data.count);
      setChats(data.data);
    }
    setChatsLoading(false);
  };

  const getPrompt = async () => {
    const url = `/api/v1/ai-context-one`;
    const { data } = await Service.get(url);

    if (data) {
      setPrompt(data?.context);
      setPromptId(data?.open_ai_context_id);
    }
  };

  const savePrompt = async (e) => {
    e.preventDefault(); // fixed minor typo

    setLoading(true);

    const url = `/api/v1/ai-context/${promptId}`;
    const { status } = await Service.put(url, { context: prompt });

    if (status === 200 || status === 201) {
      responses(false, message);
    } else {
      responses(true, message);
    }

    setLoading(false);
  };

  const responses = (isError, msg) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(msg);
  };

  const deleteMany = async () => {
    if (!selectedChatIds?.length) return;
    setLoadingDelete(true);

    for (let i = 0; i < selectedChatIds?.length; i++) {
      const url = `/api/v1/ai-chat/${selectedChatIds[i]}`;
      await Service.delete(url);

      const filtered = (chats || []).filter(
        (it) => it.open_ai_chat_id !== selectedChatIds[i]
      );
      setChats(filtered);
    }

    setLoadingDelete(false);
    setSelectedChatIds([]);
    await getChats(page, pageSize);
  };

  // -------- UI-only derived data --------
  const filteredChats = useMemo(() => {
    if (!chats) return null;
    if (!query?.trim()) return chats;
    const q = query.toLowerCase();
    return chats.filter((c) =>
      (c?.OpenAiChatTabItems?.[0]?.question || "").toLowerCase().includes(q)
    );
  }, [chats, query]);

  const allVisibleSelected =
    filteredChats?.length &&
    filteredChats.every((c) => selectedChatIds.includes(c?.open_ai_chat_id));

  const toggleSelectVisible = () => {
    if (!filteredChats?.length) return;
    if (allVisibleSelected) {
      const visibleIds = new Set(filteredChats.map((c) => c?.open_ai_chat_id));
      setSelectedChatIds((ids) => ids.filter((id) => !visibleIds.has(id)));
    } else {
      const add = filteredChats
        .map((c) => c?.open_ai_chat_id)
        .filter((id) => !selectedChatIds.includes(id));
      setSelectedChatIds([...selectedChatIds, ...add]);
    }
  };

  const clearSelection = () => setSelectedChatIds([]);

  return (
    <div className="p-3 lg:p-5 h-full overflow-y-auto rounded-b-xl bg-gradient-to-b from-white to-gray-50">
      {/* Global feedback */}
      <MyMessage
        error={error}
        message={message}
        success={success}
        onExpired={() => {
          setError(false);
          setSuccess(false);
        }}
        onClick={() => {
          setError(false);
          setSuccess(false);
          setErrorMessage("");
        }}
      />

      {/* Prompt card */}
      <form
        onSubmit={savePrompt}
        className="w-full max-w-5xl mx-auto mb-6 rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">
              AI Prompt Context
            </h2>
            <p className="text-xs text-gray-500">
              This context guides chat generation and responses.
            </p>
          </div>
          <MyActionButton
            title="Save Changes"
            loading={loading}
            className="bg-white hover:bg-white/80 py-2 px-4"
          />
        </div>
        <div className="p-4 sm:p-6">
          <TextArea
            props={{
              id: "prompt",
              title: "AI Prompt Description",
              type: "text",
              value: prompt ? prompt : "",
              onChange: (e) => setPrompt(e.target.value),
            }}
          />
          <div className="mt-2 text-xs text-gray-500 flex items-center justify-end">
            {(prompt?.length || 0).toLocaleString()} characters
          </div>
        </div>
      </form>

      {/* Chats */}
      <div className="w-full max-w-6xl mx-auto">
        {/* Toolbar */}
        <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <MyButton className="text-white flex items-center space-x-2">
              {allVisibleSelected ? (
                <FaRegCheckSquare
                  onClick={toggleSelectVisible}
                  className="text-green-600 cursor-pointer text-lg"
                />
              ) : (
                <FaRegSquare
                  onClick={toggleSelectVisible}
                  className="cursor-pointer text-white text-lg"
                />
              )}
              <p className="text-white">
                {allVisibleSelected ? "Unselect Visible" : "Select Visible"}
              </p>
            </MyButton>

            <MyButton
              onClick={deleteMany}
              className="text-white flex items-center space-x-2"
            >
              {loadingDelete ? (
                <RiRefreshLine className="text-white text-lg animate-spin" />
              ) : (
                <FaRegTrashAlt className="text-red-600 cursor-pointer text-base" />
              )}
              <p className="text-white">
                Delete {selectedChatIds?.length || 0} Item(s)
              </p>
            </MyButton>

            {selectedChatIds?.length ? (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                {selectedChatIds.length} selected
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by question..."
                className="pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
              />
            </div>

            <MyButton
              onClick={() => getChats(page, pageSize)}
              className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl px-3 py-2"
            >
              <RiRefreshLine className="text-white" />
              <span className="text-sm text-white">Refresh</span>
            </MyButton>

            {selectedChatIds?.length ? (
              <MyButton
                onClick={clearSelection}
                className="text-sm text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl px-3 py-2"
              >
                Clear
              </MyButton>
            ) : null}
          </div>
        </div>

        {/* Table card */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="mb-1 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="text-left text-gray-600">
                  <th className="px-3 py-3 font-semibold w-24">Select</th>
                  <th className="px-3 py-3 font-semibold w-64">Date Created</th>
                  <th className="px-3 py-3 font-semibold">Chat Question</th>
                  <th className="px-3 py-3 font-semibold text-center w-40">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {/* Loading skeleton */}
                {chatsLoading &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={`sk-${i}`} className="animate-pulse">
                      <td className="px-3 py-3">
                        <div className="h-5 w-5 rounded bg-gray-200" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-4 w-40 rounded bg-gray-200" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-4 w-80 rounded bg-gray-200 mb-2" />
                        <div className="h-3 w-64 rounded bg-gray-100" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-8 w-28 rounded bg-gray-200 ml-auto" />
                      </td>
                    </tr>
                  ))}

                {/* Empty state */}
                {!chatsLoading && filteredChats && filteredChats.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <div className="mx-auto max-w-sm">
                        <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                          <RiSearchLine className="text-gray-400 text-xl" />
                        </div>
                        <h3 className="mt-3 text-sm font-semibold text-gray-800">
                          No chats found
                        </h3>
                        <p className="mt-1 text-xs text-gray-500">
                          Try adjusting your search or refresh the list.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Data rows */}
                {!chatsLoading && filteredChats
                  ? filteredChats.map((oneChat, key) => {
                    const id = oneChat?.open_ai_chat_id;
                    const selected = selectedChatIds.includes(id);
                    const question =
                      oneChat?.OpenAiChatTabItems?.[0]?.question || "";
                    const created =
                      oneChat?.createdAt
                        ? new Date(oneChat.createdAt).toLocaleString()
                        : "";

                    return (
                      <tr
                        key={id || key}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-3 py-3">
                          {selected ? (
                            <MyButton
                              onClick={() =>
                                setSelectedChatIds(
                                  selectedChatIds.filter((x) => x !== id)
                                )
                              }
                            >
                              <FaRegCheckSquare className="text-green-600 cursor-pointer text-lg" />
                            </MyButton>
                          ) : (
                            <MyButton
                              onClick={() =>
                                setSelectedChatIds([
                                  ...selectedChatIds,
                                  id,
                                ])
                              }
                            >
                              <FaRegSquare className="cursor-pointer text-lg" />
                            </MyButton>
                          )}
                        </td>

                        <td className="px-3 py-3">
                          <div className="text-xs text-gray-700">{created}</div>
                        </td>

                        <td className="px-3 py-3">
                          <p className="text-gray-900 text-sm line-clamp-2">
                            {question}
                          </p>
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex justify-center">
                            <ActionButton
                              chat={oneChat}
                              reloadChats={() => getChats(page, pageSize)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                  : null}

                {/* Fallback initial row */}
                {!chats && !chatsLoading && (
                  <tr>
                    <td className="px-3 py-3">...</td>
                    <td className="px-3 py-3">...</td>
                    <td className="px-3 py-3">...</td>
                    <td className="px-3 py-3">...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-4">
          <Pagination
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            total_items={count}
            callbackFunction={(p, size) => {
              getChats(p, size);
            }}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * @author Ntavigwa Bashombe
 * @param {*} props
 * @returns
 */
const ActionButton = ({ chat, reloadChats }) => {
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);

  const deleteChat = async () => {
    try {
      setLoading(true);
      const url = `/api/v1/ai-chat/${chat?.open_ai_chat_id}`;
      const response = await Service.delete(url);
      await reloadChats();

      if (response) {
        setLoading(false);
        setShowModal(false);
        setShowPreview(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      {showPreview ? (
        <Preview
          OpenAiChatTabItems={chat?.OpenAiChatTabItems}
          setShowPreview={setShowPreview}
          loading={loading}
          onDelete={deleteChat}
        />
      ) : null}

      <DeleteModal
        name={chat?.OpenAiChatTabItems?.[0]?.question}
        onDelete={deleteChat}
        loading={loading}
        setShowModal={setShowModal}
        showModal={showModal}
      />

      <div className="flex flex-row justify-end items-center px-2 py-3 gap-2 text-xs">
        <MyButton
          title="Preview Chat"
          className="py-2 px-5 bg-white rounded-xl disabled:opacity-60"
          onClick={() => setShowPreview(true)}
          disabled={loading}
        />
        <MyButton
          title="Delete"
          className="py-2 px-5 bg-red-700 hover:bg-red-600 rounded-xl disabled:opacity-60"
          onClick={() => setShowModal(true)}
          disabled={loading}
        />
      </div>
    </>
  );
};

export default Overview;
